/*
* Created by Rishabh Kumar on 21/11/19
* */

import * as React from "react";
import AppUtils from "../utils/AppUtils";
import { FormItemMode, SEQUENTIAL_RECOMMENDATIONS_SECTIONS } from "../utils/AppConstants";
import GenericEditor from "../components/patient/edit/GenericEditor";
import PatientLabTestsView from "../components/patient/view/PatientDiagnosticLabTestsView";
import GenericItemEditor from "../components/itemEditor/GenericItemEditor";
import NewEditor from "../components/patient/edit/NewEditor";
import PrescriptionMedicineView from "../components/patient/view/PrescriptionMedicineView";
import PrescriptionDiagnosisView from "../components/patient/view/PrescriptionDiagnosisView";
import PrescriptionAdviceView from "../components/patient/view/PrescriptionAdvice";
import ModelFactory from "../models/ModelFactory";
import PrescriptionReferralView from "../components/patient/view/PrescriptionReferralView";
import { connect } from "react-redux";
import { GenericActions } from "../actions/GenericActions";
import { IAppState } from "../models/IAppState";
import IAppointmentSchedule from "../models/IAppointmentSchedule";
import GeneralApis from "../api/GeneralApis";
import { Tracker } from "../utils/Tracker";
import * as _ from "lodash";
import { EventNames, SectionContext } from "../utils/EventTrackerConstants";
import { MetaData } from "../components/common/Questionnaire";
import LocalStorageUtils from "../utils/LocalStorageUtils";

const viewTemplatesMap: { [templateName: string]: any } = {
    "prescriptionLabTest": PatientLabTestsView,
    "prescriptionMedicines": PrescriptionMedicineView,
    "prescriptionDiagnosis": PrescriptionDiagnosisView,
    "prescriptionAdvice": PrescriptionAdviceView,
    "prescriptionReferral": PrescriptionReferralView,
    "currentMedication": PrescriptionMedicineView,
}

const TEMPLATE_ID_KEY = "templateId"

interface EditorAndViewContainerProps {
    savedItems?: { item: any, mode: FormItemMode }[];
    updateData?: (item: any, index: number) => void;
    viewTemplateName?: string;
    viewTemplateProps?: any;
    widgetConfiguration?: any;
    removeItem?: (index: number) => void;
    updateList?: (listData: { item: any, mode: FormItemMode }[]) => void;
    allSuggestions?: { [key: string]: any },
    onAddNewItem?: any,
    appointmentSchedule: IAppointmentSchedule
    validationSchema?: any
    metaData?: MetaData
    fetchDataOnSelect: (diagnosis?: Array<string>) => void
    flowContext: string
    getSuggestionsSequentially?: () => void;
    handleListItemClick?: (event: any, data: { inputType: string, inputValue?: string | number, field?: string }) => void
    hideDeleteAndAddOption?: boolean;
}

interface EditorAndViewContainerState {
    showDefaultItem: boolean;
    editItems: any[],
    showEditor: boolean,
    defaultItem: any,
    error: string,
    defaultTemplateItemEdited: boolean,
    selectedTemplateInfo: any // stores template info on select of any Tags
}

export default class EditorAndViewContainer extends React.Component<EditorAndViewContainerProps, EditorAndViewContainerState> {

    constructor(props: EditorAndViewContainerProps) {
        super(props);
        this.state = {
            showDefaultItem: false,
            editItems: this.getStateSavedItemsFromProps(props),
            showEditor: false,
            defaultItem: null,
            error: null,
            defaultTemplateItemEdited: false,
            selectedTemplateInfo: {}
        }
    }

    // Kept here till getDerivedStateFromProps works by updating react-dom to 16.3.1
    componentWillReceiveProps(nextProps: EditorAndViewContainerProps) {
        if (!_.isEqual(nextProps.savedItems, this.props.savedItems)) {
            this.setState({ editItems: this.getStateSavedItemsFromProps(nextProps) })
        }
    }
    // TODO: Replace with below code once updated
    // getDerivedStateFromProps(nextProps: EditorAndViewContainerProps) {
    //     if (!_.isEqual(nextProps.savedItems, this.props.savedItems)) {
    //         return({editItems: this.getStateSavedItemsFromProps(nextProps)})
    //     }
    //     return null
    // }

    static contextType = SectionContext;

    getStateSavedItemsFromProps = (props: EditorAndViewContainerProps) => {
        return (props.savedItems || []).map((item: { item: any, mode: FormItemMode }) => item.item)
    }

    resetState = (eventName: string) => {
        LocalStorageUtils.setClickEvents({ value: eventName, widget: this.props.widgetConfiguration && this.props.widgetConfiguration.key, section: this.context })
        this.setState({
            showDefaultItem: false,
            editItems: this.getStateSavedItemsFromProps(this.props),
            showEditor: false,
            defaultItem: null,
            error: null,
            defaultTemplateItemEdited: false
        })
    }

    getPreFillParams = (selected: any, params: any[]): any => {
        let diagnosisArray: Array<any> = []
        this.state.editItems.map((diagnosis: any) => {
            diagnosisArray = [...diagnosisArray, diagnosis.disease && diagnosis.disease.internalValue]
        })
        const allParams = {
            doctorId: this.props.appointmentSchedule.doctor.id,
            uCode: selected.code || undefined,
            code: selected.code || undefined,
            subServiceType: this.props.appointmentSchedule.doctorType,
            diagnosis: selected.diagnosticDetails ? [...diagnosisArray, selected.diagnosticDetails] : diagnosisArray
        }
        let finalParams = {}
        params.map((param: string) => {
            (finalParams as any)[param] = (allParams as any)[param]
        })
        return finalParams
    }

    handleDefaultItemSelect = (selectedItem: any) => {
        switch (this.props.widgetConfiguration.type) {
            case "preFill": {
                const params = this.getPreFillParams(selectedItem, this.props.widgetConfiguration.preFillConfiguration.params)
                if (!_.isEmpty(params) && !Object.values(params).some(x => !AppUtils.isDefined(x))) {
                    this.handlePreFill(selectedItem, params, this.props.widgetConfiguration.preFillConfiguration.hyphenated)
                }
                else {
                    this.completeDefaultItemSelect(selectedItem);
                }
                break;
            }
            case "fetchData": {
                const params: any = this.getPreFillParams(selectedItem, this.props.widgetConfiguration.fetchDataConfiguration.params)
                if (!_.isEmpty(params) && !Object.values(params).some(x => !AppUtils.isDefined(x))) {
                    try {
                        this.handleFetchData(params.diagnosis)
                    } catch (err) {
                    }
                }
                this.completeDefaultItemSelect(selectedItem);
                break;
            }
            default: {
                this.completeDefaultItemSelect(selectedItem);
            }
        }
    }

    completeDefaultItemSelect = (defaultItem: any) => {
        // const defaultItem = ModelFactory.createTemplatedListDefaultItem(this.props.widgetConfiguration);
        // console.log("DEFAULT ITEM", {...defaultItem, ...selectedItem})
        const openMode = this.props.widgetConfiguration.answerList[0].openMode;
        if (openMode) {
            this.handleSaveAndAdd(defaultItem)
        } else {
            this.setState({
                defaultItem,
                showDefaultItem: false,
                showEditor: true,
                error: null,
                defaultTemplateItemEdited: false
            })
        }
    }

    handlePrefillConfig = (selectedItem: any) => {
        switch (this.props.widgetConfiguration.type) {
            case "preFill": {
                const params = this.getPreFillParams(selectedItem, this.props.widgetConfiguration.preFillConfiguration.params)
                if (!_.isEmpty(params) && !Object.values(params).some(x => !AppUtils.isDefined(x))) {
                    this.handlePreFill(selectedItem, params)
                }
                break;
            }
            case "fetchData": {
                const params: any = this.getPreFillParams(selectedItem, this.props.widgetConfiguration.fetchDataConfiguration.params)
                if (!_.isEmpty(params) && !Object.values(params).some(x => !AppUtils.isDefined(x))) {
                    try {
                        this.handleFetchData(params.diagnosis)
                    } catch (err) {
                    }
                }
                break;
            }
        }
    }

    handleEditClick = (dataItem: any, index: number) => {
        LocalStorageUtils.setClickEvents({
            field: EventNames.Edit, value: dataItem.item && dataItem.item.brand && dataItem.item.brand.displayValue,
            widget: this.props.widgetConfiguration && this.props.widgetConfiguration.key, rank: index, section: this.context
        })
        let updatedItem = { ...dataItem, mode: FormItemMode.EDIT };
        let listData = [...this.props.savedItems].map((dataItem: { item: any, mode: FormItemMode }) => {
            return { ...dataItem, mode: FormItemMode.VIEW }
        });
        listData[index] = updatedItem;
        this.props.updateList(listData);
        this.setState({
            showDefaultItem: false,
            showEditor: false,
            error: null,
            defaultItem: null
        })
    }

    handleDeleteClick = async (index: number, removeEditItem?: boolean) => {
        LocalStorageUtils.setClickEvents({ value: EventNames.Delete, widget: this.props.widgetConfiguration && this.props.widgetConfiguration.key, rank: index, section: this.context });
        this.props.removeItem(index);
        const editItems = [...this.state.editItems]
        editItems.splice(index, 1)
        if (removeEditItem) {
            this.setState({
                editItems,
                defaultItem: null,
                error: null
            }, () => {
                this.handlePrefillConfig({})
            })
        }
        else {
            this.setState({
                editItems
            })
        }
        this.loadSuggestionsSequentially();
    }

    loadSuggestionsSequentially() {
        if (this.props.widgetConfiguration.enableSequentialLoading) {
            this.props.getSuggestionsSequentially();
        }
    }

    toggleDefaultItem = () => {
        LocalStorageUtils.setClickEvents({ value: EventNames.Add_New, widget: this.props.widgetConfiguration && this.props.widgetConfiguration.key, section: this.context })
        this.setState((prevState => {
            return { showDefaultItem: !prevState.showDefaultItem }
        }))
    }

    handleDefaultItemChange = (updatedItem: any) => {
        let templateItemEditStatus = this.state.defaultTemplateItemEdited
        if (this.state.showEditor && !templateItemEditStatus && updatedItem[TEMPLATE_ID_KEY]) {
            templateItemEditStatus = true
            Tracker.track(EventNames.Template_Edited, { appointmentId: this.props.appointmentSchedule.id, doctorId: this.props.appointmentSchedule.doctor.id, templateId: updatedItem[TEMPLATE_ID_KEY] })
        }
        this.setState({
            defaultItem: updatedItem,
            defaultTemplateItemEdited: templateItemEditStatus
        })
    }

    /*
     * Function: handlePreFill
     * Description: Makes an api call on selected tag items do get the prefilled values on select of any Medicines/Lab tests etc ...
     * Params: selected(object): on which item the request should be made
     *         params(object):
     * Returns: Filtered template fields with selected item.
     * */

    handlePreFill = (selected: any, params: any, hyphenated: boolean = false) => {
        let prefilledObject = { ...selected };
        GeneralApis.getPreFillData(params, this.props.widgetConfiguration.preFillConfiguration, hyphenated)
            .then(response => {
                /*
                * Take subSection of Response if required object is nested.
                * */
                const result = this.props.widgetConfiguration.preFillConfiguration.parseResult
                    ? { ...response[this.props.widgetConfiguration.preFillConfiguration.parseResult.key], templateId: response.id }
                    : response;
                let processedFields: any = {}
                /*
                * Filtering out the null values to avoid conflicts with config values
                * */
                Object.keys(result).map((key: string) => {
                    if (result[key]) {
                        processedFields[key] = result[key]
                    }
                });
                Tracker.track(EventNames.Template_Found, { appointmentId: this.props.appointmentSchedule.id, doctorId: this.props.appointmentSchedule.doctor.id, templateId: processedFields[TEMPLATE_ID_KEY] });
                prefilledObject = { ...selected, ...processedFields };
            }, (() => {
                Tracker.track(EventNames.Template_Timed_Out, { appointmentId: this.props.appointmentSchedule.id, doctorId: this.props.appointmentSchedule.doctor.id });
            }))
            .catch((err) => {
                Tracker.track(EventNames.Template_Not_Found, { appointmentId: this.props.appointmentSchedule.id, doctorId: this.props.appointmentSchedule.doctor.id });
            })
            .finally(() => {
                this.setState({ selectedTemplateInfo: prefilledObject }, () => {
                    prefilledObject.updatingMethod && this.completeDefaultItemSelect(prefilledObject);
                });
            });
    }

    /*
    * Function: handleFetchData
    * Description: Makes an api call on selected tag items do get the templates on select of any Diagnosis/Lab tests etc ...
    * Params: params(object):
    * Returns: Filtered template fields with selected item.
    * */

    handleFetchData = (diagnosis?: Array<string>) => {
        this.props.fetchDataOnSelect(diagnosis)
    }

    trackDuplicates = (updatedItem: any) => {
        if (this.props.widgetConfiguration.uniqueKey) {
            const uniqueKey = this.props.widgetConfiguration.uniqueKey
            const itemKey = uniqueKey.split(".").reduce((a: any, v: any) => a[v], updatedItem)
            const savedItemKey = uniqueKey.split(".").reduce((a: any, v: any) => a[v], this.state.editItems[this.state.editItems.length - 1])
            const itemValue = this.props.widgetConfiguration.trackerKey ? this.props.widgetConfiguration.trackerKey.split(".").reduce((a: any, v: any) => a[v], updatedItem) : updatedItem
            if (!_.isEqual(itemKey, savedItemKey)) {
                Tracker.track(EventNames.Duplicate_Field_Detected, {
                    flow: this.props.flowContext,
                    section: this.context,
                    widget: this.props.widgetConfiguration && this.props.widgetConfiguration.key,
                    value: itemValue,
                    updatingMethod: updatedItem.updatingMethod,
                    consultationId: this.props.appointmentSchedule.id,
                    doctorId: this.props.appointmentSchedule.doctor.id,
                    specialityCode: this.props.appointmentSchedule.doctorType
                })
            }
        }
    }

    handleItemChange = (updatedItem: any, index: number) => {
        const editItems = [...this.state.editItems]
        editItems[index] = updatedItem
        if (this.props.widgetConfiguration.answerList[0].openMode)
            this.props.updateData({
                item: {
                    item: updatedItem,
                    mode: FormItemMode.EDIT
                }
            }, index)
        this.setState({
            editItems
        })
    }

    handleItemSave = (itemIndex?: number) => {
        const editItems = [...this.state.editItems];
        const updatedItem = _.isNumber(itemIndex) ? { ...editItems[itemIndex] } : null;
        if (this.validateItem(updatedItem)) {
            const index = AppUtils.isDefined(itemIndex) ? itemIndex : (this.props.savedItems || []).length;
            this.props.updateData({
                item: {
                    item: updatedItem,
                    mode: FormItemMode.VIEW
                }
            }, index);
            this.setState({
                showDefaultItem: false,
                editItems,
                showEditor: false,
                error: null
            })
        }
        LocalStorageUtils.setClickEvents({
            field: EventNames.Save, value: updatedItem && updatedItem.brand && updatedItem.brand.displayValue,
            widget: this.props.widgetConfiguration && this.props.widgetConfiguration.key, section: this.context
        })
    }

    validateItem = (item?: any) => {
        const sectionItemsValidations = this.props.validationSchema && this.props.validationSchema.properties && this.props.validationSchema.properties[this.props.widgetConfiguration.key]
        let validated = true
        const itemToValidate = item || this.state.defaultItem
        if (!_.isEmpty(sectionItemsValidations)) {
            switch (sectionItemsValidations.type) {
                case "array":
                    const requiredFields = sectionItemsValidations.items.required
                    if (!_.isEmpty(requiredFields)) {
                        requiredFields.forEach((field: any) => {
                            if (_.isEmpty(itemToValidate[field])) {
                                validated = false
                            }
                        })
                    }
            }
        }
        if (!validated) {
            this.setState({
                error: "Please add when test needs to be done"
            })
        }
        return validated
    }

    handleSave = () => {
        LocalStorageUtils.setClickEvents({
            field: EventNames.Save, value: this.state.defaultItem.brand && this.state.defaultItem.brand.displayValue,
            widget: this.props.widgetConfiguration && this.props.widgetConfiguration.key, section: this.context
        })
        if (this.validateItem()) {
            this.handleSaveAndAdd()
        }
    }

    isObjectEquivalent = (editItemObj: any, defaultItemObj: any) => {
        // Create arrays of property names
        if (!editItemObj && !defaultItemObj) {
            return EventNames.Absent_No_Edit
        }
        if (!defaultItemObj && editItemObj) {
            return EventNames.Absent_Edit
        }
        if (!editItemObj && defaultItemObj) {
            return EventNames.Pre_filled_Uneditted
        }
        const aProps = Object.getOwnPropertyNames(editItemObj);
        const bProps = Object.getOwnPropertyNames(defaultItemObj);

        // If number of properties is different,
        // objects are not equivalent
        if (aProps.length !== bProps.length) {
            return EventNames.Pre_filled_Editted;
        }
        for (let i = 0; i < aProps.length; i++) {
            const propName = aProps[i];
            // If values of same property are not equal,
            // objects are not equivalent
            if (editItemObj[propName] !== defaultItemObj[propName]) {
                return EventNames.Pre_filled_Editted;
            }
        }

        // If we made it this far, objects
        // are considered equivalent
        return EventNames.Pre_filled_Uneditted;
    }

    updateMedicationListTrack = async () => {
        const { defaultItem, selectedTemplateInfo } = this.state
        Tracker.track(EventNames.Medicine_Details_Updated, {
            medicineDisplayName: defaultItem.brand && defaultItem.brand.displayValue,
            medicineCode: selectedTemplateInfo.code,
            appointmentId: this.props.appointmentSchedule.id,
            templateId: selectedTemplateInfo.templateId && selectedTemplateInfo.templateId,
            medicineDetailsSource: selectedTemplateInfo.templateId ? EventNames.Learnt_Recommendation : EventNames.Entered_by_user,
            frequency: this.isObjectEquivalent(defaultItem.frequency, selectedTemplateInfo.frequency),
            timing: this.isObjectEquivalent(defaultItem.medicineDosage, selectedTemplateInfo.medicineDosage),
            quantity: this.isObjectEquivalent(defaultItem.medicineQuantity, selectedTemplateInfo.medicineQuantity),
            inTake: this.isObjectEquivalent(defaultItem.medicineInstructions, selectedTemplateInfo.medicineInstructions),
            duration: this.isObjectEquivalent(defaultItem.medicineDuration, selectedTemplateInfo.medicineDuration),
            comments: this.isObjectEquivalent(defaultItem.comments, selectedTemplateInfo.comments)
        })
    }

    handleSaveAndAdd = (itemToSave?: any) => {
        if (this.props.widgetConfiguration.key === "prescriptionMedicationList") {
            this.updateMedicationListTrack()
        }
        const openMode = this.props.widgetConfiguration.answerList[0].openMode;
        const updatedItem = itemToSave || { ...this.state.defaultItem };
        const index = this.props.savedItems.length;
        const editItems = [...this.state.editItems]
        editItems.push(updatedItem);
        this.props.updateData({
            item: {
                item: updatedItem,
                mode: openMode ? FormItemMode.EDIT : FormItemMode.VIEW
            }
        }, index);
        this.setState({
            showDefaultItem: true,
            selectedTemplateInfo: {},
            defaultItem: null,
            showEditor: false,
            editItems,
            error: null,
            defaultTemplateItemEdited: false
        }, () => {
            this.trackDuplicates(updatedItem);
            this.loadSuggestionsSequentially();
        })
    }

    getSuggestions = () => {
        const suggestionFieldConfig = this.props.widgetConfiguration.answerList[0].defaultItemFields.find((field: any) => field.isSuggestion);
        if (!_.isEmpty(suggestionFieldConfig) && !_.isEmpty(this.props.allSuggestions)) {
            let currentWidgetSuggestions = this.props.allSuggestions[suggestionFieldConfig.recommendationKey];
            const labelKey: string = SEQUENTIAL_RECOMMENDATIONS_SECTIONS[suggestionFieldConfig.recommendationKey]?.labelKey;
            if (_.isEmpty(currentWidgetSuggestions)) return []
            else {
                const savedTargetItems = this.props.savedItems
                    .map((savedItem) => savedItem.item)
                    .map((item: any) => {
                        if (item[suggestionFieldConfig.parentFieldKey]) {
                            if (item[suggestionFieldConfig.parentFieldKey].displayValue) {
                                return item[suggestionFieldConfig.parentFieldKey].displayValue
                            }
                            return item[suggestionFieldConfig.parentFieldKey]
                        }
                        return ""
                    })
                    .map((item: any) => item.trim());
                return currentWidgetSuggestions.filter((suggestion: any) => savedTargetItems.indexOf(AppUtils.getRecommendationTagLabel(labelKey, suggestion)?.trim()) < 0);
            }
        }
        return [];
    }

    handleEditCancel = () => {
        LocalStorageUtils.setClickEvents({
            field: EventNames.Cancel,
            widget: this.props.widgetConfiguration && this.props.widgetConfiguration.key, section: this.context
        })
        let updatedItems = [...this.props.savedItems].map((dataItem: { item: any, mode: FormItemMode }) => {
            return { ...dataItem, mode: FormItemMode.VIEW }
        });
        this.props.updateList(updatedItems);
        this.resetState(EventNames.Edit + "_" + EventNames.Cancel);
    }

    render() {
        const ViewComp = !_.isEmpty(this.props.savedItems) && this.props.viewTemplateName ? viewTemplatesMap[this.props.viewTemplateName] : null;
        const { editItems, showDefaultItem, showEditor, defaultItem } = this.state;
        const hasSavedItems = !_.isEmpty(this.props.savedItems);
        const defaultItemStatus = (showDefaultItem || !hasSavedItems) && !showEditor;
        const suggestions = defaultItemStatus ? this.getSuggestions() : [];
        const openMode: boolean = this.props.widgetConfiguration && !_.isEmpty(this.props.widgetConfiguration.answerList) && this.props.widgetConfiguration.answerList[0].openMode ? this.props.widgetConfiguration.answerList[0].openMode : false;
        return (
            <div className={"editor-and-view-container"}>
                {
                    hasSavedItems &&
                    this.props.savedItems.map((item: { item: any, mode: FormItemMode }, index: number) => {
                        if (AppUtils.isDefined(ViewComp) && item.mode === FormItemMode.VIEW) {
                            return <div key={index} className={"saved-item-container"}>
                                <ViewComp
                                    data={item.item}
                                    index={index}
                                    allData={this.props.savedItems}
                                    {...this.props.viewTemplateProps}
                                />
                                <div className={"actions-container"}>
                                    <span onClick={() => this.handleEditClick(item, index)}>
                                        <img src="/images/edit.svg" />
                                    </span>
                                    <span onClick={() => this.handleDeleteClick(index)}>
                                        <img src="/images/delete.svg" />
                                    </span>
                                </div>
                            </div>
                        } else {
                            return <div key={index} className={"list-item-editor-container"}>
                                <GenericItemEditor
                                    metaData={this.props.metaData}
                                    fields={this.props.widgetConfiguration.answerList[0].fields}
                                    widgetName={this.props.widgetConfiguration.key}
                                    basic={{
                                        handleListItemClick: this.props.handleListItemClick
                                    }}
                                    item={editItems[index]}
                                    onDataChange={(updatedItem: any) => this.handleItemChange(updatedItem, index)}
                                    showHeaderOnFields={true}
                                    index={index}
                                    items={editItems}
                                />
                                {!openMode && <div className={"cta-container"}>
                                    <div className={"error-container"}>
                                        {this.state.error && <span>{this.state.error}</span>}
                                    </div>
                                    <div className={"card-primary-action with-border"} onClick={this.handleEditCancel}>Cancel</div>
                                    <div className={"card-primary-action with-border"} onClick={() => this.handleItemSave(index)}>Save</div>
                                </div>}
                                {
                                    !this.props.hideDeleteAndAddOption &&
                                    <span onClick={() => this.handleDeleteClick(index, true)} className={"delete-icon"}>
                                        <img src="/images/delete.svg" />
                                    </span>
                                }
                            </div>
                        }
                    })
                }
                {
                    !showDefaultItem && defaultItem && showEditor &&
                    <div className={"list-item-editor-container"}>
                        <GenericItemEditor
                            metaData={this.props.metaData}
                            fields={this.props.widgetConfiguration.answerList[0].fields}
                            widgetName={this.props.widgetConfiguration.key}
                            basic={{
                                handleListItemClick: this.props.handleListItemClick
                            }}
                            item={defaultItem}
                            onDataChange={this.handleDefaultItemChange}
                            showHeaderOnFields={true}
                        />
                        <div className={"cta-container"}>
                            <div className={"error-container"}>
                                {this.state.error && <span>{this.state.error}</span>}
                            </div>
                            <div className={"card-primary-action with-border"} onClick={this.handleSave}>Save</div>
                        </div>
                        <span onClick={() => this.resetState(EventNames.Delete)} className={"delete-icon"}>
                            <img src="/images/delete.svg" />
                        </span>
                    </div>
                }
                {
                    defaultItemStatus &&
                    <div className={"list-item-editor-container"}>
                        <GenericItemEditor
                            metaData={this.props.metaData}
                            isDefault={true}
                            fields={this.props.widgetConfiguration.answerList[0].defaultItemFields}
                            widgetName={this.props.widgetConfiguration.key}
                            basic={{
                                onAddNewItem: this.props.onAddNewItem,
                                suggestionList: suggestions,
                                handleListItemClick: this.props.handleListItemClick
                            }}
                            onDataChange={this.handleDefaultItemChange}
                            item={defaultItem}
                            onDataSelect={this.handleDefaultItemSelect}
                        />
                    </div>
                }
                {
                    hasSavedItems && !defaultItem && !showEditor && !showDefaultItem && !this.props.hideDeleteAndAddOption &&
                    <div className={"add-row"}>
                        <span onClick={this.toggleDefaultItem}>Add another</span>
                    </div>
                }
            </div>
        )
    }
}